import SelectBox from 'devextreme-react/select-box';
import CustomStore from 'devextreme/data/custom_store';
import { useEffect, useMemo, useRef } from 'react';
import type { DirectoryUser } from '../../../../api/timeOff';
import { searchEnabledDirectoryUsers } from '../../../../api/timeOff';

export interface CalendarEmployeeFilterValue {
  upn: string;
  displayName: string;
}

interface TimeOffCalendarEmployeeFilterProps {
  value: CalendarEmployeeFilterValue | null;
  onChange: (value: CalendarEmployeeFilterValue | null) => void;
  compact?: boolean;
}

function userLabel(user: DirectoryUser | null | undefined): string {
  if (!user) return '';
  const email = user.mail?.trim();
  if (email && email.toLowerCase() !== user.displayName.trim().toLowerCase()) {
    return `${user.displayName} — ${email}`;
  }
  return user.displayName;
}

function cacheUser(cache: Map<string, DirectoryUser>, user: DirectoryUser) {
  cache.set(user.id, user);
  const principal = user.userPrincipalName ?? user.mail;
  if (principal) cache.set(principal.toLowerCase(), user);
}

export function TimeOffCalendarEmployeeFilter({ value, onChange, compact = false }: TimeOffCalendarEmployeeFilterProps) {
  const userCacheRef = useRef(new Map<string, DirectoryUser>());

  useEffect(() => {
    const trimmedUpn = value?.upn.trim() ?? '';
    if (!trimmedUpn) return;
    const existing = userCacheRef.current.get(trimmedUpn.toLowerCase());
    if (existing) return;
    userCacheRef.current.set(trimmedUpn.toLowerCase(), {
      id: trimmedUpn,
      displayName: value?.displayName.trim() || trimmedUpn,
      userPrincipalName: trimmedUpn,
      mail: trimmedUpn
    });
  }, [value]);

  const userStore = useMemo(
    () =>
      new CustomStore({
        key: 'id',
        load: async (loadOptions) => {
          const q = String(loadOptions.searchValue ?? '').trim();
          if (q.length < 2) return [];
          try {
            const results = await searchEnabledDirectoryUsers(q);
            for (const user of results) cacheUser(userCacheRef.current, user);
            return results;
          } catch {
            return [];
          }
        },
        byKey: async (key) => {
          const cached = userCacheRef.current.get(String(key));
          if (cached) return cached;
          const byUpn = userCacheRef.current.get(String(key).toLowerCase());
          if (byUpn) return byUpn;
          const text = String(key);
          return {
            id: text,
            displayName: value?.displayName.trim() || text,
            userPrincipalName: text,
            mail: text
          };
        }
      }),
    [value?.displayName]
  );

  const selectedId = (() => {
    const trimmedUpn = value?.upn.trim() ?? '';
    if (!trimmedUpn) return null;
    return userCacheRef.current.get(trimmedUpn.toLowerCase())?.id ?? trimmedUpn;
  })();

  return (
    <SelectBox
      className="time-off-calendar-filter__input"
      dataSource={userStore}
      displayExpr={userLabel}
      valueExpr="id"
      searchEnabled
      searchMode="contains"
      searchExpr={['displayName', 'mail', 'userPrincipalName']}
      minSearchLength={2}
      searchTimeout={350}
      showDataBeforeSearch={false}
      showClearButton
      stylingMode="outlined"
      label={compact ? undefined : 'Employee'}
      labelMode={compact ? undefined : 'floating'}
      width={compact ? 168 : '100%'}
      placeholder={compact ? 'Employee' : ''}
      noDataText="No matches found"
      dropDownOptions={{ maxHeight: 280 }}
      value={selectedId}
      onValueChanged={(e) => {
        const id = e.value as string | null | undefined;
        if (!id) {
          onChange(null);
          return;
        }
        const selected =
          userCacheRef.current.get(id) ?? userCacheRef.current.get(id.toLowerCase());
        if (selected) {
          const selectedUpn = selected.userPrincipalName ?? selected.mail ?? id;
          cacheUser(userCacheRef.current, selected);
          onChange({ upn: selectedUpn, displayName: selected.displayName });
          return;
        }
        onChange({ upn: id, displayName: '' });
      }}
    />
  );
}
